import { mediaQueryLarge, prefersReducedMotion, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {Animation | undefined} */
  #layoutAnimation;

  #filterHistoryKey = '';
  #historyPath = '';
  #filterUpdateRevision = 0;
  #failedUpdateKey = '';
  #lastUpdateOptions = {};

  connectedCallback() {
    super.connectedCallback();

    this.#historyPath = window.location.pathname;
    this.#rememberFilterHistory();
    window.addEventListener('popstate', this.#restoreFilterHistory);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#rememberFilterHistory);
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    this.#filterUpdateRevision++;
    this.#layoutAnimation?.cancel();
    window.removeEventListener('popstate', this.#restoreFilterHistory);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#rememberFilterHistory);
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #updateKey(url = new URL(window.location.href)) {
    return url.pathname + url.search;
  }

  #setUpdateState(state) {
    this.dataset.resultsUpdateState = state;
    if (state === 'error') this.setAttribute('data-results-update-failed', '');
    if (state === 'idle') this.removeAttribute('data-results-update-failed');

    for (const panel of this.querySelectorAll('[data-results-update-status]')) {
      panel.hidden = state === 'idle';
      const message = panel.querySelector('[data-results-update-message]');
      if (message) message.textContent = state === 'error' ? message.dataset.error : message.dataset.loading;
      const retry = panel.querySelector('[data-results-update-retry]');
      if (retry) {
        retry.hidden = state === 'loading' && !this.hasAttribute('data-results-update-failed');
        retry.disabled = state === 'loading';
      }
    }
  }

  async updateFilters(options = {}) {
    const revision = ++this.#filterUpdateRevision;
    const retainedComponents = this.hasAttribute('data-immediate-filter-updates')
      ? [...this.querySelectorAll('*')].filter(element => element instanceof Component) : [];
    const url = new URL(window.location.href);
    const key = this.#updateKey(url);
    const isCurrentUpdate = () =>
      revision === this.#filterUpdateRevision && this.isConnected && key === this.#updateKey();
    const render = (viewTransition = false) => {
      if (!isCurrentUpdate()) return;
      return sectionRenderer.renderSection(this.sectionId, { url, viewTransition, shouldRender: isCurrentUpdate });
    };
    const retryButton = document.activeElement?.closest('[data-results-update-retry]');
    const retryDialog = retryButton?.closest('dialog');
    const restoreRetryFocus = () => retryButton &&
      (document.activeElement === retryButton || document.activeElement === document.body) &&
      (!retryDialog || retryDialog.open);
    this.#lastUpdateOptions = options;
    this.#failedUpdateKey = '';
    this.setFilterUpdatePending(true);
    this.#setUpdateState('loading');
    this.setAttribute('aria-busy', 'true');

    try {
      if (options.transitionBeforeFetch) {
        await startViewTransition(() => render(), ['product-grid']);
      } else {
        await render(options.viewTransition);
      }
      if (!isCurrentUpdate()) return;
      this.updatedCallback();
      for (const component of retainedComponents) {
        if (this.contains(component)) component.updatedCallback();
      }
      this.#setUpdateState('idle');
      this.setFilterUpdatePending(false);
      if (restoreRetryFocus()) {
        const dialog = this.querySelector('dialog[open]');
        const targets = dialog
          ? dialog.querySelectorAll('.facets__see-results, .facets-drawer__close')
          : this.querySelectorAll('button:not([data-results-update-retry])');
        [...targets].find((target) => target.getClientRects().length)?.focus({ preventScroll: true });
      }
    } catch {
      if (!isCurrentUpdate()) return;
      this.#failedUpdateKey = key;
      this.#setUpdateState('error');
      if (restoreRetryFocus() && retryButton.getClientRects().length) retryButton.focus({ preventScroll: true });
    } finally {
      if (revision === this.#filterUpdateRevision) this.removeAttribute('aria-busy');
    }
  }

  retryFilters() {
    if (!this.#failedUpdateKey || this.#failedUpdateKey !== this.#updateKey()) return;
    this.dispatchEvent(new FilterUpdateEvent(new URL(window.location.href).searchParams));
    return this.updateFilters(this.#lastUpdateOptions);
  }

  #getFilterHistoryKey() {
    const url = new URL(window.location.href);
    const parameters = new URLSearchParams();
    // Infinite scrolling updates `page` without replacing the loaded product cards.
    for (const [name, value] of url.searchParams) {
      if (name.startsWith('filter.') || name.startsWith('options.') || ['sort_by', 'q', 'type'].includes(name)) {
        parameters.append(name, value);
      }
    }
    parameters.sort();
    return parameters.toString();
  }

  #rememberFilterHistory = () => {
    this.#filterHistoryKey = this.#getFilterHistoryKey();
  };

  #restoreFilterHistory = () => {
    if (window.location.pathname !== this.#historyPath) return;
    const key = this.#getFilterHistoryKey();
    if (key === this.#filterHistoryKey) return;

    this.#rememberFilterHistory();
    // Restore the complete destination without racing pagination's section requests.
    window.location.reload();
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#changeLayout(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #changeLayout = (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    this.#layoutAnimation?.cancel();
    // Animate the live grid after applying the layout. Unlike a resized card
    // snapshot, opacity and translation keep text at its actual font size.
    this.#setLayout(value);

    if (!prefersReducedMotion()) {
      this.#layoutAnimation = grid.animate(
        [
          { opacity: 0.35, transform: 'translateY(6px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 220, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
      );
    }

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Applies the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    this.#layoutAnimation?.cancel();
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
